import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { resolveAvatar } from "@/lib/api";
import { Loader, EmptyState } from "@/components/shared";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";

const CATS = [
  { icon: "Trophy", label: "Story Stars", color: "#EAB308" },
  { icon: "Flame", label: "On Fire", color: "#FC633B" },
  { icon: "Sparkles", label: "Rising Stars", color: "#662D91" },
];

export default function Leaderboard() {
  const { data, isLoading } = useQuery({ queryKey: ["leaderboard"], queryFn: async () => (await api.get("/leaderboard")).data });
  if (isLoading) return <Loader />;
  if (!data.enabled) return <EmptyState icon="Trophy" title="Leaderboard is hidden" text="Your coach has turned off the leaderboard for now." />;

  const entries = data.entries || [];
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);
  const heights = { 1: "h-28", 2: "h-20", 3: "h-16" };

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div>
        <h1 className="font-head text-4xl text-csh-blue">Leaderboard 🏆</h1>
        <p className="text-muted-foreground">Every Story Champ is a star — keep shining!</p>
      </div>

      <div className="flex gap-2">
        {CATS.map((c) => { const I = Icons[c.icon]; return (
          <div key={c.label} className="flex flex-1 items-center gap-2 rounded-2xl bg-white px-3 py-2 shadow-sm border border-black/5">
            <I size={18} color={c.color} /> <span className="text-xs font-bold text-csh-grey">{c.label}</span>
          </div>
        ); })}
      </div>

      {/* Podium */}
      <div className="flex items-end justify-center gap-3">
        {podiumOrder.map((e) => (
          <motion.div key={e.id} initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="flex flex-1 flex-col items-center" style={{ maxWidth: 130 }}>
            <img src={resolveAvatar(e.avatar)} alt="" className={`h-14 w-14 rounded-full object-cover ring-4 ${e.rank === 1 ? "ring-csh-orange" : "ring-csh-purple/40"}`} />
            <div className="mt-1 font-bold text-csh-blue text-sm text-center truncate w-full">{e.name.split(" ")[0]}</div>
            <div className="text-xs font-bold text-csh-orange">⭐ {e.points}</div>
            <div className={`mt-1 w-full rounded-t-2xl gradient-purple ${heights[e.rank]} flex items-start justify-center pt-2 font-head text-2xl text-white`}>
              {e.rank === 1 ? "👑" : e.rank}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="space-y-2">
        {rest.map((e) => (
          <div key={e.id} data-testid={`lb-row-${e.rank}`}
            className={`flex items-center gap-3 rounded-2xl px-4 py-3 shadow-sm border ${e.id === data.my_id ? "bg-csh-orange/10 border-csh-orange" : "bg-white border-black/5"}`}>
            <span className="font-head text-lg text-csh-grey w-6">{e.rank}</span>
            <img src={resolveAvatar(e.avatar)} alt="" className="h-10 w-10 rounded-full object-cover" />
            <span className="flex-1 font-bold text-csh-blue">{e.name}{e.id === data.my_id && " (You)"}</span>
            <span className="flex items-center gap-1 text-sm font-bold text-csh-purple"><Icons.Medal size={14} /> {e.badges}</span>
            <span className="font-head text-lg text-csh-orange">⭐ {e.points}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
